import type { Screen } from '../api.js';
import {
  Badge,
  EmptyState,
  LinkButton,
  PageHeader,
  Panel,
  PanelBody,
  SectionLabel,
} from '../ui/index.js';

const PHASES = ['P0', 'P1', 'P2', 'P3', 'P4', 'P5'] as const;

/**
 * Roadmap placeholder. Empty states always state the next action (docs/09 §7):
 * each stub names its phase, the document that specifies it, and what will land here.
 */
export function StubPage({ screen }: { readonly screen: Screen }): React.ReactElement {
  const quickWin = screen.phase.startsWith('P1 (quick win)');
  const phaseKey = PHASES.find((phase) => screen.phase.startsWith(phase));

  return (
    <div>
      <PageHeader
        index={screen.index}
        title={screen.title}
        purpose={`Écran de la console MaPara (docs/09-REVIEW-CONSOLE.md §1). Il n'est pas encore branché : sa livraison est planifiée en phase ${screen.phase} du roadmap.`}
        meta={[
          { label: 'Phase', value: screen.phase },
          { label: 'Spécification', value: 'docs/09-REVIEW-CONSOLE.md' },
          { label: 'Roadmap', value: 'docs/12-ROADMAP.md' },
        ]}
      />

      <EmptyState
        title={`Écran prévu — livré en phase ${screen.phase}`}
        hint={
          quickWin
            ? "Le premier fichier Sage (format générique) sera généré dès l'ingestion du catalogue (P1)."
            : 'Cette vue suit la spécification docs/09-REVIEW-CONSOLE.md et arrive avec sa phase du roadmap (docs/12-ROADMAP.md).'
        }
        action={
          <LinkButton href="/" variant="outline" size="sm">
            Retour au tableau de bord
          </LinkButton>
        }
      />

      <Panel className="mt-4">
        <PanelBody>
          <SectionLabel>Position dans le roadmap</SectionLabel>
          <ol className="mt-3 flex flex-wrap gap-2">
            {PHASES.map((phase) => {
              const done =
                phaseKey !== undefined && PHASES.indexOf(phase) < PHASES.indexOf(phaseKey);
              const now = phase === phaseKey;
              return (
                <li key={phase} className="flex items-center gap-2">
                  <Badge tone={now ? 'signal' : done ? 'good' : 'muted'}>
                    {phase}
                    {now ? ' · vous êtes ici' : done ? ' · livré' : ''}
                  </Badge>
                  {phase !== PHASES[PHASES.length - 1] && (
                    <span aria-hidden="true" className="h-2 w-4 paper-grain text-line-strong" />
                  )}
                </li>
              );
            })}
          </ol>
          <p className="mt-3 text-xs leading-relaxed text-ink-soft">
            Aucune donnée n'est écrite depuis un écran non livré : la console n'active l'écriture
            que lorsque le moteur correspondant, ses tests et son audit sont en place.
          </p>
        </PanelBody>
      </Panel>
    </div>
  );
}
